import type { ControlBridgeQueueItem } from '../deriveViewModel';
import type { DiagnoseScopeHealthRow } from '../deriveDiagnoseHealth';
import { entropyStateLabel } from '../entropyLanguage';
import { healthBarFillPct } from '../missionControl/missionControlHelpers';
import { isP0, isP1, isPriorityP0ToP2, sortByPriority } from '../queueHelpers';
import { isAssignedToUser } from '../attentionRouting';
import type { PersonalLayerUserContext } from '../personalLayer/buildPersonalLayerViewModels';
import { itemIdMatchesSource } from '../../../utils/itemIdPlane';

export interface GlanceTileFraming {
  more: string;
  blocking: string;
  where: string;
}

export interface PrimaryConstraintModel {
  title: string;
  scopeLabel: string;
  scopeId: string;
  unlockLine: string;
}

export interface PersonalLoadLeverageModel {
  decisionLatency: string;
  blockersCleared: string | null;
  timeInExecution: string;
  /** False when every metric would be unassessed — collapse the YOU metrics strip. */
  showMetricsStrip: boolean;
}

/** Diagnose priority lists show this many rows per reveal; the rest stay on the page. */
export const DIAGNOSE_PRIORITY_LIST_CHUNK = 5;

/** Whole-token terminal statuses only — avoid substring hits on incomplete / not done. */
function looksClearedStatus(phrase: string | undefined): boolean {
  if (!phrase) return false;
  const normalized = phrase.trim().toLowerCase().replace(/[_/-]+/g, ' ');
  if (/\b(incomplete|unresolved|not\s+done|open|active|created|drafted)\b/.test(normalized)) {
    return false;
  }
  return /\b(closed|cleared|resolved)\b/.test(normalized);
}

/** P0–P2 for the Diagnose You stacked list (not assignment-filtered). */
export function scopedPriorityAlertList(
  issues: ControlBridgeQueueItem[],
  work: ControlBridgeQueueItem[]
): ControlBridgeQueueItem[] {
  const seen = new Set<string>();
  const pool = [...issues, ...work].filter((item) => {
    if (!isPriorityP0ToP2(item.priority)) return false;
    if (!item.itemId || seen.has(item.itemId)) return false;
    seen.add(item.itemId);
    return true;
  });
  return sortByPriority(pool);
}

/** Assigned-to-you P0/P1 for the YOU strip. Empty without a user. Display chunking is UI-only. */
export function scopedPriorityFocusList(
  issues: ControlBridgeQueueItem[],
  work: ControlBridgeQueueItem[],
  options?: {
    cap?: number;
    user?: PersonalLayerUserContext;
    excludeItemIds?: Iterable<string>;
  }
): ControlBridgeQueueItem[] {
  if (!options?.user) return [];
  const exclude = new Set(options.excludeItemIds ?? []);
  const pool = [...issues, ...work].filter((item) => {
    if (!(isP0(item.priority) || isP1(item.priority))) return false;
    if (exclude.has(item.itemId)) return false;
    return itemAssignedToUser(item, options.user);
  });
  const sorted = sortByPriority(pool);
  if (options.cap == null) return sorted;
  return sorted.slice(0, Math.max(0, options.cap));
}

export function scopedP0Issues(issues: ControlBridgeQueueItem[]): ControlBridgeQueueItem[] {
  return sortByPriority(issues.filter((item) => isP0(item.priority)));
}

export function scopedP0Work(work: ControlBridgeQueueItem[]): ControlBridgeQueueItem[] {
  return sortByPriority(work.filter((item) => isP0(item.priority)));
}

/** Spec: entropy bars sorted worst last (highest strain at bottom). */
export function sortDiagnoseRowsWorstLast(rows: DiagnoseScopeHealthRow[]): DiagnoseScopeHealthRow[] {
  return [...rows].sort((a, b) => {
    const ea = a.entropy ?? -1;
    const eb = b.entropy ?? -1;
    return ea - eb;
  });
}

function pickBlockingItem(
  row: DiagnoseScopeHealthRow
): ControlBridgeQueueItem | null {
  const sortedIssues = sortByPriority(row.issues);
  const p0 = sortedIssues.find((item) => isP0(item.priority));
  if (p0) return p0;
  if (sortedIssues[0]) return sortedIssues[0];
  const sortedWork = sortByPriority(row.activities);
  return sortedWork[0] ?? null;
}

export function subdomainBlockingLabel(row: DiagnoseScopeHealthRow): string {
  const blocker = pickBlockingItem(row);
  if (blocker?.title) return blocker.title;
  if (row.openIssues > 0) {
    return `${row.openIssues} open issue${row.openIssues === 1 ? '' : 's'} in scope`;
  }
  if (row.activeActivities > 0) {
    return `${row.activeActivities} active activit${row.activeActivities === 1 ? 'y' : 'ies'} in scope`;
  }
  return 'No named constraint in this scope';
}

export function subdomainFraming(row: DiagnoseScopeHealthRow): GlanceTileFraming {
  const blocker = pickBlockingItem(row);
  const cfPct =
    row.correctionFactor != null ? `${Math.round(row.correctionFactor * 100)}% operating efficiency` : 'efficiency';
  return {
    more: `Improve ${cfPct} by closing issues and completing active work in this subdomain`,
    blocking: subdomainBlockingLabel(row),
    where: row.label,
  };
}

export interface DiagnoseThreeQuestions {
  objective: string;
  needsFixing: string;
  leverage: string;
}

/** Per-subdomain Diagnose questions — issues, not Exo work lists. */
export function diagnoseThreeQuestions(row: DiagnoseScopeHealthRow): DiagnoseThreeQuestions {
  const stateLabel = entropyStateLabel(row.entropy);
  const entropyLabel =
    stateLabel != null
      ? stateLabel
      : row.entropy == null
        ? 'Entropy not assessed'
        : `Entropy ${row.entropy}`;
  const capability =
    row.correctionFactor != null
      ? `${Math.round(row.correctionFactor * 100)}% capability`
      : 'capability not assessed';
  const issueTitles = sortByPriority(row.issues)
    .map((item) => item.title?.trim())
    .filter((title): title is string => Boolean(title));
  return {
    objective: `${entropyLabel} · ${capability}`,
    needsFixing:
      issueTitles[0] ??
      (row.openIssues > 0
        ? `${row.openIssues} open issue${row.openIssues === 1 ? '' : 's'}`
        : 'No named issues in this scope'),
    leverage:
      issueTitles.length > 0
        ? `Issues allocated to ${row.label} feed this leverage`
        : `No allocated issues in ${row.label}`,
  };
}

export interface DiagnoseResourcePositionRow {
  scopeId: string;
  scopeLabel: string;
  count: number;
  names: string[];
}

/** Named assignees already on Diagnose rows — empty when none are bound. */
export function diagnoseResourcePosition(
  rows: DiagnoseScopeHealthRow[]
): DiagnoseResourcePositionRow[] {
  return rows
    .map((row) => {
      const names = new Set<string>();
      for (const item of [...row.issues, ...row.activities]) {
        const label = item.assignedResourceLabel?.trim();
        if (label) names.add(label);
      }
      return {
        scopeId: row.scopeId,
        scopeLabel: row.label,
        count: names.size,
        names: Array.from(names),
      };
    })
    .filter((row) => row.count > 0);
}

function primaryUnlockLine(
  blocker: ControlBridgeQueueItem | null,
  row: DiagnoseScopeHealthRow
): string {
  if (blocker?.improvementTitle) {
    return `Addressing this restores flow for ${blocker.improvementTitle}`;
  }
  if (blocker?.issueSummary) {
    return blocker.issueSummary;
  }
  if (row.openIssues > 0) {
    return `Clearing ${row.openIssues} open issue${row.openIssues === 1 ? '' : 's'} reduces entropy in ${row.label}`;
  }
  return `Completing active work in ${row.label} improves session throughput`;
}

export function buildPrimaryConstraint(
  rows: DiagnoseScopeHealthRow[],
  issues: ControlBridgeQueueItem[],
  work: ControlBridgeQueueItem[]
): PrimaryConstraintModel | null {
  const sortedRows = [...rows].sort((a, b) => (b.entropy ?? -1) - (a.entropy ?? -1));
  const hottest = sortedRows.find((row) => row.entropy != null);
  if (hottest) {
    const blocker = pickBlockingItem(hottest);
    if (blocker) {
      return {
        title: blocker.title,
        scopeLabel: hottest.label,
        scopeId: hottest.scopeId,
        unlockLine: primaryUnlockLine(blocker, hottest),
      };
    }
  }

  const sortedIssues = sortByPriority(issues);
  const sortedWork = sortByPriority(work);

  const sessionBlocker =
    sortedIssues.find((item) => isP0(item.priority)) ??
    sortedIssues.find((item) => isP1(item.priority)) ??
    sortedWork.find((item) => isP0(item.priority));

  if (sessionBlocker) {
    const row =
      sortedRows.find((r) =>
        [...r.issues, ...r.activities].some((item) =>
          itemIdMatchesSource(sessionBlocker.itemId, item)
        )
      ) ?? sortedRows[0];
    return {
      title: sessionBlocker.title,
      scopeLabel: row?.label ?? 'Session-wide',
      scopeId: row?.scopeId ?? sessionBlocker.filterScope ?? sessionBlocker.domain ?? 'company',
      unlockLine: primaryUnlockLine(sessionBlocker, row ?? sortedRows[0]!),
    };
  }

  for (const row of sortedRows) {
    const blocker = pickBlockingItem(row);
    if (blocker) {
      return {
        title: blocker.title,
        scopeLabel: row.label,
        scopeId: row.scopeId,
        unlockLine: primaryUnlockLine(blocker, row),
      };
    }
  }

  const worstRow = sortedRows[0];
  if (worstRow && (worstRow.entropy ?? 0) >= 5) {
    return {
      title: subdomainBlockingLabel(worstRow),
      scopeLabel: worstRow.label,
      scopeId: worstRow.scopeId,
      unlockLine: primaryUnlockLine(null, worstRow),
    };
  }

  return null;
}

export function vgrComponentFillPct(actual: number | null, ideal: number | null): number {
  if (actual == null || ideal == null || ideal <= 0) return 0;
  return Math.round(Math.max(0, Math.min(100, (actual / ideal) * 100)));
}

function daysSince(isoDate: string): number | null {
  const parsed = Date.parse(isoDate);
  if (Number.isNaN(parsed)) return null;
  const diffMs = Date.now() - parsed;
  return Math.max(0, Math.round(diffMs / (1000 * 60 * 60 * 24)));
}

function itemAssignedToUser(item: ControlBridgeQueueItem, user?: PersonalLayerUserContext): boolean {
  if (!user) return false;
  const ref = user.resourceProfileRef?.trim() || user.id?.trim();
  if (ref && isAssignedToUser(item, ref)) return true;
  const userLabel = user.label?.trim().toLowerCase() ?? '';
  const label = item.assignedResourceLabel?.trim().toLowerCase() ?? '';
  if (userLabel && label && (label === userLabel || label.includes(userLabel))) return true;
  const userId = user.id?.trim().toLowerCase() ?? '';
  const res = item.res?.toLowerCase() ?? '';
  if (userId && (res.includes(userId) || label.includes(userId))) return true;
  return false;
}

export function buildPersonalLoadLeverage(
  issues: ControlBridgeQueueItem[],
  work: ControlBridgeQueueItem[],
  user?: PersonalLayerUserContext
): PersonalLoadLeverageModel {
  const prioritized = sortByPriority(issues.filter((item) => isP0(item.priority) || isP1(item.priority)));
  let decisionLatency = '—';
  for (const item of prioritized) {
    if (item.statusDate) {
      const days = daysSince(item.statusDate);
      if (days != null) {
        decisionLatency = `${days}d`;
        break;
      }
    }
  }
  if (decisionLatency === '—' && prioritized.length > 0) {
    decisionLatency = `${prioritized.length} open`;
  }

  const pool = [...work, ...issues];
  const assigned = pool.filter((item) => itemAssignedToUser(item, user));
  const timeInExecution =
    pool.length > 0 ? `${Math.round((assigned.length / pool.length) * 100)}%` : '—';

  const clearedCount = pool.filter((item) => looksClearedStatus(item.statusPhrase)).length;
  const blockersCleared = clearedCount > 0 ? String(clearedCount) : null;

  const showMetricsStrip = !(
    decisionLatency === '—' &&
    blockersCleared == null &&
    timeInExecution === '—'
  );

  return {
    decisionLatency,
    blockersCleared,
    timeInExecution,
    showMetricsStrip,
  };
}

export function entropyBarTone(entropy: number | null): 'good' | 'mid' | 'weak' | 'muted' {
  if (entropy == null) return 'muted';
  if (entropy <= 4) return 'good';
  if (entropy <= 6) return 'mid';
  return 'weak';
}

export { healthBarFillPct };
